/**
 * labs.js — hoe een laboratorium eruitziet in de backoffice.
 *
 * De backend kent dezelfde sleutels (mv-backend-worker/src/labs.js) en bepaalt wat er mag;
 * dit bestand gaat alleen over weergave. Een koppeling met een sleutel die hier niet in
 * staat verdwijnt niet uit beeld: `labPresentation` maakt er dan een tegel van op basis van
 * de sleutel zelf, zodat een nieuw lab zichtbaar is voordat er een logo voor is.
 */
const LABS = {
  sgs: { key: 'sgs', name: 'SGS', logo: '/labs/sgs.png', accent: '#E8762D', certificaat: true },
  eurofins: { key: 'eurofins', name: 'Eurofins', logo: '/labs/eurofins.png', accent: '#1E3A8A' },
};

/**
 * Kunnen we het analysecertificaat (PDF) bij dit lab opvragen? Alleen SGS levert het via de
 * koppeling; bij een ander lab zou de knop altijd op 'mislukt' uitkomen.
 */
export function heeftCertificaat(labKey) {
  return !!LABS[String(labKey || '').toLowerCase()]?.certificaat;
}

/** Labs die een sysadmin kan toevoegen, in de volgorde waarin ze getoond worden. */
export const SELECTABLE_LABS = [LABS.sgs, LABS.eurofins];

/**
 * Het monstertype van een labopdracht: welke soort monsters erin gaat. Het lab koppelt zijn
 * pakketten hieraan, dus alleen wat de backend aan een monstersoort van het lab kan koppelen
 * (SUPPORTED_MATRICES in mv-backend-worker/src/lab-sample-types.js). Een type zonder koppeling
 * levert een pakketkiezer zonder pakketten op.
 *
 * Eén type per opdracht: het aanleverbestand kent één monstersoort voor de hele opdracht.
 * Asfalt gaat daarom in een eigen labopdracht, naast die voor de grond.
 */
export const MONSTERTYPES = [
  ['grond', 'Grond'],
  ['grondwater', 'Grondwater'],
  ['waterbodem', 'Waterbodem'],
  ['asfalt', 'Asfalt'],
];

/**
 * De keuzes voor een keuzelijst, met het huidige type erbij als dat er niet (meer) in staat.
 * Oude opdrachten dragen soms 'water' of 'asbest'; die stil op 'Grond' laten springen zou bij
 * de eerstvolgende wijziging hun type overschrijven.
 */
export function monstertypeKeuzes(huidig) {
  if (!huidig || MONSTERTYPES.some(([v]) => v === huidig)) return MONSTERTYPES;
  return [...MONSTERTYPES, [huidig, `${huidig.charAt(0).toUpperCase()}${huidig.slice(1)} (geen pakketten)`]];
}

export function labPresentation(labKey) {
  const key = String(labKey || '').toLowerCase();
  return LABS[key] || {
    key,
    name: labKey || 'Onbekend lab',
    logo: null,
    accent: '#6B7280',
  };
}

/**
 * De stip achter een labnaam. Groen betekent: klaar voor echt werk. Oranje: het werkt, maar
 * tegen de testomgeving van het lab, dus opdrachten komen niet in de echte administratie.
 * Dat onderscheid is het hele punt van de stip; zonder kleur zie je niet dat een koppeling
 * weliswaar "actief" is maar nergens toe leidt.
 */
export function labStatusDot(connection) {
  const status = connection?.status;
  if (status === 'invalid') return { color: '#DC2626', label: 'Verificatie mislukt' };
  if (status === 'active') {
    return connection.environment === 'production'
      ? { color: '#16A34A', label: 'Actief' }
      : { color: '#D97706', label: 'Testomgeving' };
  }
  if (status === 'inactive') return { color: '#9CA3AF', label: 'Uit' };
  return { color: '#9CA3AF', label: 'Concept' };
}

/** Vaste legenda onder de bedrijvenlijst. */
export const LAB_STATUS_LEGEND = [
  { color: '#16A34A', label: 'Actief' },
  { color: '#D97706', label: 'Testomgeving' },
  { color: '#DC2626', label: 'Verificatie mislukt' },
];

/**
 * Waar je een verstuurde labopdracht bevestigt: @MIS, de portal van SGS.
 *
 * Versturen zet de opdracht bij SGS klaar, maar neemt hem niet in behandeling: hij staat daar
 * als concept tot iemand hem in @MIS bevestigt. Dat is bewust -- zo kun je er nog iets aan
 * veranderen voordat het lab begint -- maar het betekent wel dat een opdracht die je hier
 * verstuurd hebt stil blijft liggen als je die stap overslaat.
 *
 * Twee omgevingen, twee adressen. De testomgeving hangt aan de Nederlandse vestiging en heeft
 * daarom een taalpad in de URL; productie niet.
 */
const ATMIS_ORDERS = {
  test: 'https://atmis-test.nl.sgs.com/nl-NL/SGS/Continental/Order.atmis',
  production: 'https://atmis.sgs.com/SGS/Continental/Order.atmis',
};

/** Het adres van de opdrachtenlijst in @MIS, of null als we dit lab daar niet kennen. */
export function atmisOrderUrl(connection) {
  if (!connection || connection.labKey !== 'sgs') return null;
  return ATMIS_ORDERS[connection.environment === 'production' ? 'production' : 'test'];
}

/**
 * Wacht deze opdracht nog op bevestiging in @MIS?
 *
 * 'Concept' en 'New' zijn de namen die SGS aan een nog niet bevestigde opdracht geeft. Weten we
 * de stand nog niet (net verstuurd, statussen nog niet opgehaald), dan geldt hetzelfde: net
 * verstuurd betekent onbevestigd, en dat is precies het moment waarop je het moet weten.
 */
export function wachtOpBevestiging(order) {
  if (!order || !(order.sentAt || order.status === 'sent')) return false;
  const naam = String(order.labStatusName || '').toLowerCase();
  if (!naam) return true;
  return /concept|new|nieuw/.test(naam);
}

/**
 * De stand die het lab meldt, in het Nederlands en met een kleur.
 *
 * SGS stuurt per opdracht een Engelse naam (New, Concept, Busy, Confirmed, InProgress,
 * Reported) en per batch de Nederlandse vertaling ervan. Wij tonen de opdrachtstatus, dus
 * vertalen we zelf -- 'Busy' in een Nederlands scherm leest als een storing.
 *
 * De kleuren zijn die van onze eigen statuskolom, zodat dezelfde toestand er in beide kolommen
 * hetzelfde uitziet: geel betekent dat er op jóu gewacht wordt, blauw dat het lab bezig is,
 * groen dat het klaar is, rood dat er iets mis is. Een naam die we niet kennen blijft grijs
 * staan met zijn oorspronkelijke tekst: gokken levert een kleur op die niets betekent.
 *
 * De uitleg verschijnt als tooltip. Daar staat vooral in wat je ermee moet: bij 'Concept' ligt
 * de bal bij jou, bij 'Bezig' bij het lab.
 */
const LAB_STATUSSEN = [
  { test: /^(concept|new|nieuw)$/i, label: (n) => (/new|nieuw/i.test(n) ? 'Nieuw' : 'Concept'),
    klasse: 'warn',
    uitleg: 'De opdracht staat klaar bij het lab maar is nog niet bevestigd. Bevestig hem in @MIS; tot die tijd begint het lab niet.' },
  { test: /^(busy|bezig)$/i, label: () => 'Bezig', klasse: 'bezig',
    uitleg: 'Het lab is bezig deze opdracht te verwerken. Blijft deze status staan, neem dan contact op met het lab.' },
  { test: /^(confirmed|bevestigd)$/i, label: () => 'Bevestigd', klasse: 'bezig',
    uitleg: 'Het lab heeft de opdracht aangenomen. De monsters gaan in behandeling.' },
  { test: /^(inprogress|in bewerking)$/i, label: () => 'In bewerking', klasse: 'bezig',
    uitleg: 'De monsters worden geanalyseerd. Zodra het lab rapporteert, kun je de uitslag ophalen.' },
  { test: /^(reported|gerapporteerd)$/i, label: () => 'Gerapporteerd', klasse: 'ok',
    uitleg: 'Het lab heeft gerapporteerd: de uitslag en het analysecertificaat liggen klaar.' },
  { test: /(cancel|geannuleerd|reject|afgekeurd|error|fout)/i, label: (n) => n, klasse: 'bad',
    uitleg: 'Het lab heeft deze opdracht niet in behandeling genomen. Neem contact op met het lab.' },
  { test: /^pricetemplatecalculation$/i, label: () => 'Prijsberekening', klasse: 'bezig',
    uitleg: 'Het lab berekent de prijs van deze opdracht.' },
];

/** @returns {{label: string, klasse: string, uitleg: string}|null} */
export function labStatusPresentatie(naam) {
  const tekst = String(naam || '').trim();
  if (!tekst) return null;
  const hit = LAB_STATUSSEN.find(s => s.test.test(tekst));
  if (!hit) return { label: tekst, klasse: 'neutraal', uitleg: `Status bij het lab: ${tekst}.` };
  return { label: hit.label(tekst), klasse: hit.klasse, uitleg: hit.uitleg };
}
